import { useState } from 'react';
import dayjs from 'dayjs';
import { CalendarIcon, FileText, Plus } from 'lucide-react';
import { useParams } from 'react-router';

import { type ReceiptsFilters, useReceipts } from '@/api/receipt/use-receipts';
import { ReceiptBaseDtoStatusEnum } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import AddReceiptModal from '@/features/receipts/add-receipt-modal/add-receipt-modal.tsx';
import ReceiptDetailDrawer from '@/features/receipts/receipt-detail-drawer/receipt-detail-drawer.tsx';
import ReceiptRow from '@/features/receipts/receipt-row/receipt-row.tsx';
import AppContent from '@/layout/app-content';

const defaultFilters = (): ReceiptsFilters => ({
  dateFrom: dayjs().startOf('month').toDate(),
  dateTo: dayjs().endOf('day').toDate(),
});

const Receipts = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [filters, setFilters] = useState<ReceiptsFilters>(defaultFilters);
  const { receiptId } = useParams();

  const { data: receipts, isLoading } = useReceipts(filters);

  const updateFilter = (patch: Partial<ReceiptsFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
  };

  if (!receipts?.length && isLoading) {
    return (
      <AppContent title="Receipts">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">Loading receipts...</div>
          </CardContent>
        </Card>
      </AppContent>
    );
  }

  return (
    <AppContent title="Receipts">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Receipts</CardTitle>
              <CardDescription>Manage and view all your expense receipts</CardDescription>
            </div>
            <Button onClick={() => setIsAddModalOpen(true)} data-icon="inline-end">
              Add Receipt
              <Plus className="size-3.5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Party</label>
              <Input
                className="w-40"
                placeholder="Search party"
                value={filters.party || ''}
                onChange={(event) => updateFilter({ party: event.target.value || undefined })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Item contains</label>
              <Input
                className="w-40"
                placeholder="Search items"
                value={filters.itemSearch || ''}
                onChange={(event) => updateFilter({ itemSearch: event.target.value || undefined })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Status</label>
              <select
                className="h-7 rounded-md border border-input bg-input/20 dark:bg-input/30 px-2 text-xs"
                value={filters.status || ''}
                onChange={(event) => updateFilter({ status: event.target.value || undefined })}
              >
                <option value="">All statuses</option>
                <option value={ReceiptBaseDtoStatusEnum.Created}>Created</option>
                <option value={ReceiptBaseDtoStatusEnum.Loaded}>Loaded</option>
                <option value={ReceiptBaseDtoStatusEnum.Processed}>Processed</option>
                <option value={ReceiptBaseDtoStatusEnum.Failed}>Failed</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Assigned</label>
              <select
                className="h-7 rounded-md border border-input bg-input/20 dark:bg-input/30 px-2 text-xs"
                value={filters.assigned || ''}
                onChange={(event) => updateFilter({ assigned: event.target.value || undefined })}
              >
                <option value="">All</option>
                <option value="true">Assigned</option>
                <option value="false">Not assigned</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Date</label>
              <Popover>
                <PopoverTrigger>
                  <Button variant="outline" className="justify-start px-2.5 font-normal w-48">
                    <CalendarIcon data-icon="inline-start" />
                    {filters.dateFrom ? (
                      <>
                        {dayjs(filters.dateFrom).format('DD.MM.YYYY')}
                        {filters.dateTo && <> - {dayjs(filters.dateTo).format('DD.MM.YYYY')}</>}
                      </>
                    ) : (
                      <span>All</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 gap-0" align="start">
                  <div className="flex flex-wrap px-3 pt-3 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        updateFilter({
                          dateFrom: dayjs().startOf('month').toDate(),
                          dateTo: dayjs().endOf('day').toDate(),
                        })
                      }
                    >
                      This month
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        updateFilter({
                          dateFrom: dayjs().subtract(1, 'month').startOf('month').toDate(),
                          dateTo: dayjs().subtract(1, 'month').endOf('month').toDate(),
                        })
                      }
                    >
                      Last month
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateFilter({ dateFrom: undefined, dateTo: undefined })}
                    >
                      All
                    </Button>
                  </div>
                  <Calendar
                    mode="range"
                    defaultMonth={filters.dateFrom}
                    selected={{ from: filters.dateFrom, to: filters.dateTo }}
                    onSelect={(date) =>
                      updateFilter({
                        dateFrom: date?.from ? dayjs(date.from).startOf('day').toDate() : undefined,
                        dateTo: date?.to ? dayjs(date.to).endOf('day').toDate() : undefined,
                      })
                    }
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>
      <div className="flex flex-col gap-2">
        <h2 className="text-base mb-1">All Receipts</h2>
        {receipts?.map((receipt) => (
          <ReceiptRow key={receipt.uuid} receipt={receipt} />
        ))}
        {!receipts?.length && (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <CardTitle className="text-lg mb-2">No receipts found</CardTitle>
            <CardDescription>Add your first receipt or adjust the filters above.</CardDescription>
          </div>
        )}
      </div>
      {isAddModalOpen && <AddReceiptModal onClose={() => setIsAddModalOpen(false)} />}
      {receiptId && <ReceiptDetailDrawer receiptId={receiptId} />}
    </AppContent>
  );
};

export default Receipts;
